import { useDeploymentMode } from "../../../hooks/useDeploymentMode";
import AppIconButton from "../../../components/form/AppIconButton";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useTranslation } from "react-i18next";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogTitle,
} from "../../../components/dialog";
import { PageHeader, PageListContainer } from "../../../components/Page";
import PackageLimitHint from "../../../components/PackageLimitHint";
import { AppAutocomplete, AppButton, AppFileButton, AppTextField } from "../../../components/form";
import OpenSearchIndexPicker from "../../../components/OpenSearchIndexPicker";
import {
  AppActionsRow,
  AppFormField,
  AppFormGrid,
  PageSectionCard,
} from "../../../components/layout";
import { formRowButtonSx } from "../../../lib/ui/formLayout";
import { guideService } from "../../../services/guideService";
import type { GuideStorageUsage } from "../../../services/packageLimitsService";
import {
  mappingDiscoveryService,
  type GuideDraft,
} from "../../../services/mappingDiscoveryService";
import {
  adaptorDiscoveryService,
  type AdaptorDiscoveryDraft,
} from "../../../services/adaptorDiscoveryService";
import {
  adminDataAdaptorService,
  flattenAdaptorLeafObjects,
  type AdaptorObjectNode,
  type DataAdaptor,
} from "../../../services/adminDataAdaptorService";
import type { GuideDocument } from "../../../types/guide";
import GuideAnalysisPanel from "../components/GuideAnalysisPanel";
import GuideAnalysisViewDialog from "../components/GuideAnalysisViewDialog";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";
import { formatBytesMb, usePackageLimits } from "../../../hooks/usePackageLimits";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { parseBackendNotificationDetails } from "../../../utils/notification";
import {
  clearRagTransferPending,
  getPendingRagTransferIds,
  isRagTransferInFlight,
  markRagTransferPending,
  syncPendingRagTransfers,
} from "../pendingTransfers";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";

function objectLabel(obj: AdaptorObjectNode): string {
  return obj.qualifiedName || obj.name;
}

function formatGuideBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusColor(status?: string): "default" | "success" | "warning" | "error" | "info" {
  switch (status) {
    case "READY":
    case "INGESTED":
    case "APPROVED":
      return "success";
    case "INGESTING":
    case "UPLOADED":
    case "DRAFT":
      return "warning";
    case "FAILED":
    case "REJECTED":
      return "error";
    default:
      return "default";
  }
}

function errorMessage(e: unknown): string {
  if (e && typeof e === "object" && "response" in e) {
    const data = (e as { response?: { data?: unknown } }).response?.data;
    const parsed = parseBackendNotificationDetails(data);
    if (parsed.message) return parsed.message;
  }
  return e instanceof Error ? e.message : String(e);
}

function AdaptorDiscoveryTab() {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const [adaptors, setAdaptors] = useState<DataAdaptor[]>([]);
  const [adaptorId, setAdaptorId] = useState("");
  const [schemaObjects, setSchemaObjects] = useState<AdaptorObjectNode[]>([]);
  const [selectedObjects, setSelectedObjects] = useState<AdaptorObjectNode[]>([]);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [drafts, setDrafts] = useState<AdaptorDiscoveryDraft[]>([]);
  const [busy, setBusy] = useState(false);
  const [transferringId, setTransferringId] = useState<string | null>(null);
  const [pendingTransferIds, setPendingTransferIds] = useState<Set<string>>(() =>
    getPendingRagTransferIds()
  );
  const [error, setError] = useState("");
  const [analysisPanelOpen, setAnalysisPanelOpen] = useState(false);
  const [changeMessage, setChangeMessage] = useState("");
  const [checkingChanges, setCheckingChanges] = useState(false);
  const [activeDraft, setActiveDraft] = useState<AdaptorDiscoveryDraft | null>(null);
  const [analysisText, setAnalysisText] = useState("");
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [viewAnalysisOpen, setViewAnalysisOpen] = useState(false);
  const [viewAnalysisLoading, setViewAnalysisLoading] = useState(false);
  const [viewAnalysisText, setViewAnalysisText] = useState("");
  const [viewAnalysisMeta, setViewAnalysisMeta] = useState<{
    adaptorName?: string;
    draftTitle?: string;
  }>({});
  const [objectsDialog, setObjectsDialog] = useState<{ title: string; names: string[] } | null>(null);

  const refresh = useCallback(async () => {
    const [list, page] = await Promise.all([
      adminDataAdaptorService.list(),
      adaptorDiscoveryService.listDrafts(0, 50),
    ]);
    setAdaptors(list || []);
    const nextDrafts = page.content || [];
    setDrafts(nextDrafts);
    setPendingTransferIds(syncPendingRagTransfers(nextDrafts));
  }, []);

  useEffect(() => {
    refresh().catch((e) => setError(errorMessage(e)));
  }, [refresh]);

  useEffect(() => {
    const inFlight =
      drafts.some((d) => d.status === "INGESTING") || pendingTransferIds.size > 0;
    if (!inFlight) return;
    const timer = window.setInterval(() => {
      refresh().catch(() => undefined);
    }, 2500);
    return () => window.clearInterval(timer);
  }, [drafts, pendingTransferIds, refresh]);

  useEffect(() => {
    setSelectedObjects([]);
    setSchemaObjects([]);
    if (!adaptorId) return;
    let cancelled = false;
    setSchemaLoading(true);
    setError("");
    adminDataAdaptorService
      .metadataTree(adaptorId)
      .then((tree) => {
        if (!cancelled) setSchemaObjects(flattenAdaptorLeafObjects(tree.roots));
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setSchemaObjects([]);
          setError(errorMessage(e));
        }
      })
      .finally(() => {
        if (!cancelled) setSchemaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adaptorId]);

  /** Compares live schemas with the latest drafts; warehouse adaptors re-index only the changed tables. */
  const checkSchemaChanges = async () => {
    setCheckingChanges(true);
    setError("");
    setChangeMessage("");
    try {
      const changes = await adaptorDiscoveryService.checkChanges(false);
      setChangeMessage(
        changes.length === 0
          ? t("guide.adaptorNoChanges")
          : t("guide.adaptorChanges", { count: changes.length })
      );
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setCheckingChanges(false);
    }
  };

  const discover = async () => {
    if (!adaptorId || selectedObjects.length === 0) return;
    setBusy(true);
    setError("");
    try {
      const names = selectedObjects.map(objectLabel).filter(Boolean);
      await adaptorDiscoveryService.discover(adaptorId, names);
      await refresh();
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const openAnalysisPanel = (draft: AdaptorDiscoveryDraft) => {
    setActiveDraft(draft);
    setAnalysisText(draft.analysisText ?? "");
    setAnalysisPanelOpen(true);
  };

  const closeAnalysisPanel = () => {
    setAnalysisPanelOpen(false);
    setActiveDraft(null);
    setAnalysisText("");
  };

  const generateAnalysis = async () => {
    if (!activeDraft?.id) return;
    setGeneratingAnalysis(true);
    setError("");
    try {
      const result = await adaptorDiscoveryService.generateAnalysis(activeDraft.id);
      setAnalysisText(result.analysisText ?? "");
      setActiveDraft((prev) =>
        prev
          ? {
              ...prev,
              analysisText: result.analysisText,
              analysisApprovedAt: undefined,
              analysisApprovedBy: undefined,
            }
          : prev
      );
      await refresh();
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  const approveAnalysis = async () => {
    if (!activeDraft?.id || !analysisText.trim()) return;
    setSavingAnalysis(true);
    setError("");
    try {
      await adaptorDiscoveryService.updateAnalysis(activeDraft.id, analysisText.trim());
      const updated = await adaptorDiscoveryService.approveAnalysis(
        activeDraft.id,
        analysisText.trim()
      );
      setActiveDraft(updated);
      await refresh();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.approveAnalysis"),
            content: t("guide.analysisApprovedSuccess"),
          },
        })
      );
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setSavingAnalysis(false);
    }
  };

  const openViewAnalysis = async (draft: AdaptorDiscoveryDraft) => {
    if (!draft.id) return;
    setViewAnalysisOpen(true);
    setViewAnalysisLoading(true);
    setViewAnalysisText("");
    setViewAnalysisMeta({
      adaptorName: draft.adaptorName,
      draftTitle: draft.draftTitle,
    });
    try {
      const analysis = await adaptorDiscoveryService.getAnalysis(draft.id);
      setViewAnalysisText(analysis.analysisText);
      setViewAnalysisMeta({
        adaptorName: analysis.adaptorName || draft.adaptorName,
        draftTitle: analysis.draftTitle || draft.draftTitle,
      });
    } catch (e: unknown) {
      setError(errorMessage(e));
      setViewAnalysisOpen(false);
    } finally {
      setViewAnalysisLoading(false);
    }
  };

  const deleteDraft = async (draft: AdaptorDiscoveryDraft) => {
    if (!draft.id) return;
    try {
      await confirmDelete({
        itemName: draft.draftTitle || draft.adaptorName,
        titleKey: "guide.deleteAdaptorDraftTitle",
        descriptionKey: "guide.deleteAdaptorDraftDescription",
      });
    } catch (e: unknown) {
      if (isCancelled(e)) return;
      throw e;
    }
    setBusy(true);
    setError("");
    try {
      await adaptorDiscoveryService.remove(draft.id);
      await refresh();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("common.delete"),
            content: t("guide.deleteAdaptorDraftSuccess"),
          },
        })
      );
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const reanalyze = async (draft: AdaptorDiscoveryDraft) => {
    if (!draft.id) return;
    setBusy(true);
    setGeneratingAnalysis(true);
    setError("");
    try {
      const updated = await adaptorDiscoveryService.reanalyze(draft.id);
      setActiveDraft(updated);
      setAnalysisText(updated.analysisText ?? "");
      setAnalysisPanelOpen(true);
      await refresh();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.reanalyze"),
            content: t("guide.reanalyzeSuccess"),
          },
        })
      );
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setGeneratingAnalysis(false);
      setBusy(false);
    }
  };

  const reingest = async (id?: string) => {
    if (!id) return;
    markRagTransferPending(id);
    setPendingTransferIds(getPendingRagTransferIds());
    setTransferringId(id);
    setBusy(true);
    setError("");
    setDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, status: "INGESTING", rejectionReason: undefined } : d))
    );
    try {
      const updated = await adaptorDiscoveryService.reingest(id);
      await refresh();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.reingestToRag"),
            content:
              updated.status === "INGESTED"
                ? t("guide.reingestToRagSuccess")
                : t("guide.adaptorTransferIngesting"),
          },
        })
      );
    } catch (e: unknown) {
      clearRagTransferPending(id);
      setPendingTransferIds(getPendingRagTransferIds());
      setError(errorMessage(e));
      await refresh().catch(() => undefined);
    } finally {
      setTransferringId(null);
      setBusy(false);
    }
  };

  const transfer = async (id?: string) => {
    if (!id) return;
    markRagTransferPending(id);
    setPendingTransferIds(getPendingRagTransferIds());
    setTransferringId(id);
    setBusy(true);
    setError("");
    setDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, status: "INGESTING", rejectionReason: undefined } : d))
    );
    try {
      const updated = await adaptorDiscoveryService.transferToRag(id);
      await refresh();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.transferToRag"),
            content:
              updated.status === "INGESTED"
                ? t("guide.transferSuccess")
                : t("guide.adaptorTransferIngesting"),
          },
        })
      );
    } catch (e: unknown) {
      clearRagTransferPending(id);
      setPendingTransferIds(getPendingRagTransferIds());
      setError(errorMessage(e));
      await refresh().catch(() => undefined);
    } finally {
      setTransferringId(null);
      setBusy(false);
    }
  };

  return (
    <>
    <PageSectionCard title={t("guide.tabAdaptor")}>
      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      <Stack spacing={2} maxWidth={640}>
        <AppAutocomplete<DataAdaptor>
          label={t("guide.adaptorSelect")}
          options={adaptors.filter((a) => a.enabled && a.id)}
          value={adaptors.find((a) => a.id === adaptorId) ?? null}
          autoHighlight
          openOnFocus
          getOptionLabel={(a) => `${a.name} (${a.type})`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, option) => setAdaptorId(option?.id ?? "")}
        />
        <AppAutocomplete<AdaptorObjectNode, true>
          multiple
          label={t("guide.adaptorObjects")}
          options={schemaObjects}
          value={selectedObjects}
          disabled={!adaptorId || schemaLoading}
          loading={schemaLoading}
          autoHighlight
          openOnFocus
          filterSelectedOptions
          getOptionLabel={objectLabel}
          isOptionEqualToValue={(a, b) => objectLabel(a) === objectLabel(b)}
          onChange={(_, options) => setSelectedObjects(options)}
          helperText={
            schemaLoading
              ? t("guide.adaptorObjectsLoading")
              : adaptorId
                ? t("guide.adaptorObjectsHint")
                : t("guide.adaptorObjectsPickAdaptor")
          }
          renderOption={(props, option) => (
            <li {...props} key={objectLabel(option)}>
              <Stack spacing={0} sx={{ py: 0.25 }}>
                <Typography variant="body2">{objectLabel(option)}</Typography>
                {option.kind ? (
                  <Typography variant="caption" color="text.secondary">
                    {option.kind}
                  </Typography>
                ) : null}
              </Stack>
            </li>
          )}
        />
        {schemaLoading ? <CircularProgress size={22} /> : null}
        <AppButton
          onClick={() => void discover()}
          disabled={busy || !adaptorId || selectedObjects.length === 0 || schemaLoading}
        >
          {t("guide.discoverAdaptor")}
        </AppButton>
        <AppButton
          variant="outlined"
          color="warning"
          onClick={() => void checkSchemaChanges()}
          disabled={busy || checkingChanges}
        >
          {checkingChanges ? t("common.loading") : t("guide.checkAdaptorChanges")}
        </AppButton>
      </Stack>
      {changeMessage && (
        <Alert severity="info" sx={{ mt: 2 }} onClose={() => setChangeMessage("")}>
          {changeMessage}
        </Alert>
      )}
      <Table size="small" sx={{ mt: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t("guide.adaptorColumn")}</TableCell>
            <TableCell>{t("guide.adaptorObjects")}</TableCell>
            <TableCell>{t("guide.createdDate")}</TableCell>
            <TableCell>{t("guide.status")}</TableCell>
            <TableCell align="right">{t("common.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {drafts.map((d) => {
            const inFlight = isRagTransferInFlight(d, pendingTransferIds, transferringId);
            const displayStatus = inFlight ? "INGESTING" : d.status || "DRAFT";
            return (
            <TableRow key={d.id}>
              <TableCell>
                <Typography variant="body2">{d.adaptorName || d.draftTitle}</Typography>
                {d.rejectionReason && !inFlight ? (
                  <Typography variant="caption" color="error" display="block">
                    {d.rejectionReason}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>
                {d.objectNames && d.objectNames.length > 0 ? (
                  <Tooltip title={t("guide.adaptorObjectsShow")}>
                    <AppIconButton
                      size="small"
                      aria-label={t("guide.adaptorObjectsShow")}
                      onClick={() =>
                        setObjectsDialog({
                          title: d.adaptorName || d.draftTitle || t("guide.adaptorObjects"),
                          names: d.objectNames || [],
                        })
                      }
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </AppIconButton>
                  </Tooltip>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell>
                {d.createdDate ? new Date(d.createdDate).toLocaleString() : "—"}
              </TableCell>
              <TableCell>
                <Chip size="small" color={statusColor(displayStatus)} label={displayStatus} />
              </TableCell>
              <TableCell align="right">
                {inFlight ? (
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    <CircularProgress size={18} />
                    <Typography variant="caption" color="text.secondary">
                      {t("guide.adaptorTransferIngesting")}
                    </Typography>
                    <AppButton size="small" variant="outlined" onClick={() => void openViewAnalysis(d)}>
                      {t("guide.viewAnalysis")}
                    </AppButton>
                  </Stack>
                ) : null}
                {!inFlight && d.status === "DRAFT" && d.id && (
                  <Box sx={custom.components.guide.draftActionsRow}>
                    <AppButton size="small" variant="outlined" disabled={busy} onClick={() => void reanalyze(d)}>
                      {t("guide.reanalyze")}
                    </AppButton>
                    <AppButton size="small" variant="outlined" onClick={() => openAnalysisPanel(d)}>
                      {d.analysisApprovedAt ? t("guide.reviewAnalysis") : t("guide.generateAnalysis")}
                    </AppButton>
                    <AppButton
                      size="small"
                      variant="contained"
                      disabled={!d.analysisApprovedAt || busy}
                      onClick={() => void transfer(d.id)}
                    >
                      {t("guide.transferToRag")}
                    </AppButton>
                    <AppButton
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={busy}
                      onClick={() => void deleteDraft(d)}
                    >
                      {t("common.delete")}
                    </AppButton>
                  </Box>
                )}
                {!inFlight && d.status === "INGESTED" && d.id && (
                  <Box sx={custom.components.guide.draftActionsRow}>
                    <AppButton size="small" variant="outlined" disabled={busy} onClick={() => void reanalyze(d)}>
                      {t("guide.reanalyze")}
                    </AppButton>
                    <AppButton
                      size="small"
                      variant="contained"
                      disabled={busy || !d.analysisApprovedAt}
                      onClick={() => void reingest(d.id)}
                    >
                      {t("guide.reingestToRag")}
                    </AppButton>
                    <AppButton size="small" variant="outlined" onClick={() => void openViewAnalysis(d)}>
                      {t("guide.viewAnalysis")}
                    </AppButton>
                    <AppButton
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={busy}
                      onClick={() => void deleteDraft(d)}
                    >
                      {t("common.delete")}
                    </AppButton>
                  </Box>
                )}
                {!inFlight && d.status === "REJECTED" && d.id && (
                  <AppButton
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={busy}
                    onClick={() => void deleteDraft(d)}
                  >
                    {t("common.delete")}
                  </AppButton>
                )}
              </TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </PageSectionCard>

    <GuideAnalysisPanel
      open={analysisPanelOpen}
      indexName={activeDraft?.adaptorName || activeDraft?.draftTitle}
      analysisText={analysisText}
      analysisApproved={Boolean(activeDraft?.analysisApprovedAt)}
      generating={generatingAnalysis}
      saving={savingAnalysis}
      onChange={setAnalysisText}
      onClose={closeAnalysisPanel}
      onGenerate={() =>
        void (activeDraft?.status === "INGESTED" && activeDraft ? reanalyze(activeDraft) : generateAnalysis())
      }
      onApprove={() => void approveAnalysis()}
    />
    <GuideAnalysisViewDialog
      open={viewAnalysisOpen}
      indexName={viewAnalysisMeta.adaptorName}
      draftTitle={viewAnalysisMeta.draftTitle}
      analysisText={viewAnalysisText}
      loading={viewAnalysisLoading}
      onClose={() => setViewAnalysisOpen(false)}
    />
    <AppDialog
      open={Boolean(objectsDialog)}
      onClose={() => setObjectsDialog(null)}
      maxWidth="sm"
      fullWidth
    >
      <AppDialogTitle>
        {t("guide.adaptorObjectsDialogTitle", { name: objectsDialog?.title || "" })}
      </AppDialogTitle>
      <AppDialogContent>
        <Stack spacing={0.75}>
          {(objectsDialog?.names || []).map((name, index) => (
            <Typography key={`${name}-${index}`} variant="body2">
              {name}
            </Typography>
          ))}
        </Stack>
      </AppDialogContent>
      <AppDialogActions>
        <AppDialogCancelButton onClick={() => setObjectsDialog(null)}>
          {t("common.close")}
        </AppDialogCancelButton>
      </AppDialogActions>
    </AppDialog>
    </>
  );
}

export type AiGuideSection = "documents" | "mapping" | "adaptor";
const SECTION_TAB: Record<AiGuideSection, number> = { documents: 0, mapping: 1, adaptor: 2 };

/**
 * With no `section` the page shows its own three tabs. A hub page can pass `section` to show just one
 * of them (the hub provides the tab row).
 */
export default function AiGuidePage({ section }: { section?: AiGuideSection } = {}) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const packageLimits = usePackageLimits();
  const deployment = useDeploymentMode();
  const lockedTab = section ? SECTION_TAB[section] : undefined;
  const [tabState, setTab] = useState(0);
  const tab = lockedTab ?? tabState;
  const [docs, setDocs] = useState<GuideDocument[]>([]);
  const [drafts, setDrafts] = useState<GuideDraft[]>([]);
  const [title, setTitle] = useState("");
  const [indexName, setIndexName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [mappingChanges, setMappingChanges] = useState<
    Array<{ indexName: string; currentMappingHash?: string }>
  >([]);
  const [error, setError] = useState("");
  const [analysisPanelOpen, setAnalysisPanelOpen] = useState(false);
  const [activeDraft, setActiveDraft] = useState<GuideDraft | null>(null);
  const [analysisText, setAnalysisText] = useState("");
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [transferringId, setTransferringId] = useState<string | null>(null);
  const [pendingTransferIds, setPendingTransferIds] = useState<Set<string>>(() =>
    getPendingRagTransferIds()
  );
  const [viewAnalysisOpen, setViewAnalysisOpen] = useState(false);
  const [viewAnalysisLoading, setViewAnalysisLoading] = useState(false);
  const [viewAnalysisText, setViewAnalysisText] = useState("");
  const [viewAnalysisMeta, setViewAnalysisMeta] = useState<{ indexName?: string; draftTitle?: string }>({});
  const [storageUsage, setStorageUsage] = useState<GuideStorageUsage | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuDraft, setMenuDraft] = useState<GuideDraft | null>(null);
  const [viewDocOpen, setViewDocOpen] = useState(false);
  const [viewDocLoading, setViewDocLoading] = useState(false);
  const [viewDocText, setViewDocText] = useState("");
  const [viewDocTitle, setViewDocTitle] = useState("");

  const refreshUsage = useCallback(async () => {
    try {
      setStorageUsage(await guideService.usage());
    } catch {
      setStorageUsage(null);
    }
  }, []);

  const refreshDocs = async () => {
    const page = await guideService.list(0, 50);
    setDocs(page.content || []);
  };

  const viewGuideRecord = async (doc: GuideDocument) => {
    if (!doc.id) return;
    setViewDocTitle(doc.title || doc.fileName || "");
    setViewDocText("");
    setViewDocOpen(true);
    setViewDocLoading(true);
    try {
      const content = await guideService.content(doc.id);
      setViewDocText(content.text || "");
    } catch (err: unknown) {
      setViewDocText("");
      setError(err instanceof Error ? err.message : t("guide.viewDataFailed"));
    } finally {
      setViewDocLoading(false);
    }
  };

  const refreshDrafts = async () => {
    const page = await mappingDiscoveryService.listDrafts(0, 50);
    const nextDrafts = page.content || [];
    setDrafts(nextDrafts);
    setPendingTransferIds(syncPendingRagTransfers(nextDrafts));
  };

  const refresh = async () => {
    await Promise.all([refreshDocs(), refreshDrafts(), refreshUsage()]);
  };

  const storageFull =
    storageUsage != null &&
    !storageUsage.unlimited &&
    storageUsage.remainingBytes != null &&
    storageUsage.remainingBytes <= 0;
  const storagePct =
    storageUsage != null && storageUsage.maxBytes != null && storageUsage.maxBytes > 0
      ? Math.min(100, Math.round((storageUsage.usedBytes / storageUsage.maxBytes) * 100))
      : 0;

  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  useEffect(() => {
    const hasIngesting =
      docs.some((d) => d.status === "INGESTING") ||
      drafts.some((d) => d.status === "INGESTING") ||
      pendingTransferIds.size > 0;
    if (!hasIngesting) return;
    const interval = window.setInterval(() => {
      refresh().catch(console.error);
    }, 2500);
    return () => window.clearInterval(interval);
  }, [docs, drafts, pendingTransferIds]);

  useEffect(() => {
    if (tab === 0) {
      refreshDocs().catch(console.error);
    }
  }, [tab]);

  const uploadFile = async (file: File) => {
    if (storageFull) {
      setError(t("guide.storageQuotaFull"));
      return;
    }
    if (
      storageUsage &&
      storageUsage.maxSingleFileBytes != null &&
      file.size > storageUsage.maxSingleFileBytes
    ) {
      setError(t("guide.storageSingleFileCap", { max: formatGuideBytes(storageUsage.maxSingleFileBytes) }));
      return;
    }
    if (
      storageUsage &&
      !storageUsage.unlimited &&
      storageUsage.remainingBytes != null &&
      file.size > storageUsage.remainingBytes
    ) {
      setError(t("guide.storageQuotaFull"));
      return;
    }
    setUploading(true);
    setError("");
    try {
      await guideService.upload(file, title || undefined);
      setTitle("");
      await Promise.all([refreshDocs(), refreshUsage()]);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: { color: "success", icon: "check", title: t("guide.upload"), content: t("guide.uploadSuccess") },
        })
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const deleteDoc = async (doc: GuideDocument) => {
    if (!doc.id) return;
    try {
      await confirmDelete({ itemName: doc.title || doc.fileName });
      await guideService.remove(doc.id);
      await refreshDocs();
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const discoverMapping = async () => {
    if (!indexName.trim()) return;
    setDiscovering(true);
    setError("");
    try {
      await mappingDiscoveryService.discover(indexName.trim());
      setIndexName("");
      await refreshDrafts();
      setTab(1);
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.discoverIndex"),
            content: t("guide.discoverSuccess"),
          },
        })
      );
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setDiscovering(false);
    }
  };

  const approveDraft = async (id: string) => {
    markRagTransferPending(id);
    setPendingTransferIds(getPendingRagTransferIds());
    setTransferringId(id);
    setError("");
    setDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, status: "INGESTING" } : d))
    );
    try {
      await mappingDiscoveryService.approve(id);
      await refreshDrafts();
      await refreshDocs();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.transferToRag"),
            content: t("guide.adaptorTransferIngesting"),
          },
        })
      );
    } catch (err: unknown) {
      clearRagTransferPending(id);
      setPendingTransferIds(getPendingRagTransferIds());
      setError(err instanceof Error ? err.message : String(err));
      await refreshDrafts().catch(() => undefined);
    } finally {
      setTransferringId(null);
    }
  };

  const openAnalysisPanel = (draft: GuideDraft) => {
    setActiveDraft(draft);
    setAnalysisText(draft.analysisText ?? "");
    setAnalysisPanelOpen(true);
  };

  const closeAnalysisPanel = () => {
    setAnalysisPanelOpen(false);
    setActiveDraft(null);
    setAnalysisText("");
  };

  const generateAnalysis = async () => {
    if (!activeDraft?.id) return;
    setGeneratingAnalysis(true);
    setError("");
    try {
      const result = await mappingDiscoveryService.generateAnalysis(activeDraft.id);
      setAnalysisText(result.analysisText ?? "");
      setActiveDraft((prev) =>
        prev ? { ...prev, analysisText: result.analysisText, analysisApprovedAt: undefined, analysisApprovedBy: undefined } : prev
      );
      await refreshDrafts();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  const approveAnalysis = async () => {
    if (!activeDraft?.id || !analysisText.trim()) return;
    setSavingAnalysis(true);
    setError("");
    try {
      await mappingDiscoveryService.updateAnalysis(activeDraft.id, analysisText.trim());
      const updated = await mappingDiscoveryService.approveAnalysis(activeDraft.id, analysisText.trim());
      setActiveDraft(updated);
      await refreshDrafts();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.approveAnalysis"),
            content: t("guide.analysisApprovedSuccess"),
          },
        })
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingAnalysis(false);
    }
  };

  const rejectDraft = async (id: string) => {
    try {
      await confirmDelete({
        itemName: id,
        titleKey: "guide.rejectDraft",
        descriptionKey: "confirm.deleteDescriptionGeneric",
      });
      await mappingDiscoveryService.reject(id);
      await refreshDrafts();
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const openViewAnalysis = async (draft: GuideDraft) => {
    if (!draft.id) return;
    setViewAnalysisOpen(true);
    setViewAnalysisLoading(true);
    setViewAnalysisText("");
    setViewAnalysisMeta({ indexName: draft.indexName, draftTitle: draft.draftTitle });
    try {
      const analysis = await mappingDiscoveryService.getAnalysis(draft.id);
      setViewAnalysisText(analysis.analysisText);
      setViewAnalysisMeta({ indexName: analysis.indexName, draftTitle: analysis.draftTitle });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      setViewAnalysisOpen(false);
    } finally {
      setViewAnalysisLoading(false);
    }
  };

  const deleteRejectedDraft = async (draft: GuideDraft) => {
    if (!draft.id) return;
    try {
      await confirmDelete({
        itemName: draft.indexName || draft.draftTitle,
        titleKey: "guide.deleteRejectedTitle",
        descriptionKey: "guide.deleteRejectedDescription",
      });
      await mappingDiscoveryService.deleteRejected(draft.id);
      await refreshDrafts();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.deleteRejected"),
            content: t("guide.deleteRejectedSuccess"),
          },
        })
      );
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const deleteIngestedDraft = async (draft: GuideDraft) => {
    if (!draft.id) return;
    try {
      await confirmDelete({
        itemName: draft.indexName || draft.draftTitle,
        titleKey: "guide.deleteIngestedTitle",
        descriptionKey: "guide.deleteIngestedDescription",
      });
      await mappingDiscoveryService.deleteIngested(draft.id);
      await refreshDrafts();
      await refreshDocs();
      window.dispatchEvent(
        new CustomEvent("showGlobalNotification", {
          detail: {
            color: "success",
            icon: "check",
            title: t("guide.deleteIngested"),
            content: t("guide.deleteIngestedSuccess"),
          },
        })
      );
    } catch (error) {
      if (isCancelled(error)) return;
      throw error;
    }
  };

  const checkMappingChanges = async () => {
    setError("");
    try {
      const changes = await mappingDiscoveryService.checkChanges(true);
      setMappingChanges(changes);
      await refreshDrafts();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <PageListContainer>
      <PageHeader title={section === "adaptor" ? t("nav.adaptorDiscovery") : t("guide.title")} />
        {error && (
          <Alert severity="error" sx={custom.pages.shared.alertMarginBottom2} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {lockedTab === undefined && (
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={custom.common.tabsMarginBottomTwo}>
            <Tab icon={<DescriptionOutlinedIcon fontSize="small" />} iconPosition="start" label={t("guide.tabDocuments")} />
            <Tab icon={<AccountTreeOutlinedIcon fontSize="small" />} iconPosition="start" label={t("guide.tabMapping")} />
            <Tab icon={<UploadOutlinedIcon fontSize="small" />} iconPosition="start" label={t("guide.tabAdaptor")} />
          </Tabs>
        )}

        {tab === 0 && (
          <>
            {packageLimits && (
              <PackageLimitHint group="store" capped={packageLimits.guideStorageMaxBytes != null}>
                {t("packageLimits.guideDocuments", {
                  mb: formatBytesMb(
                    packageLimits.guideStorageMaxBytes,
                    t("packageLimits.unlimited")
                  ),
                })}
              </PackageLimitHint>
            )}
            {storageUsage && !deployment.onPrem && (
              <Box sx={{ mb: 2 }}>
                <PageSectionCard title={t("guide.storageQuotaTitle")}>
                  <Alert severity={storageFull ? "warning" : "info"} sx={{ mb: 1.5 }}>
                    {storageUsage.unlimited
                      ? t("guide.storageQuotaUnlimited", {
                          used: formatGuideBytes(storageUsage.usedBytes),
                        })
                      : t("guide.storageQuotaUsed", {
                          used: formatGuideBytes(storageUsage.usedBytes),
                          max: formatGuideBytes(storageUsage.maxBytes || 0),
                          remaining: formatGuideBytes(storageUsage.remainingBytes || 0),
                        })}
                  </Alert>
                  {!storageUsage.unlimited && (
                    <LinearProgress
                      variant="determinate"
                      value={storagePct}
                      color={storageFull ? "warning" : "primary"}
                      sx={{ mb: 1.5, height: 8, borderRadius: 1 }}
                    />
                  )}
                  {storageUsage.maxSingleFileBytes != null && (
                    <Typography variant="body2" color="text.secondary">
                      {t("guide.storageSingleFileCap", {
                        max: formatGuideBytes(storageUsage.maxSingleFileBytes),
                      })}
                    </Typography>
                  )}
                  {storageFull && (
                    <Alert severity="warning" sx={{ mt: 1.5 }}>
                      {t("guide.storageQuotaFull")}
                    </Alert>
                  )}
                </PageSectionCard>
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
            <PageSectionCard title={t("guide.upload")}>
              <AppFormGrid alignItems="flex-end">
                <AppFormField size={{ xs: 12, sm: "grow" }}>
                  <AppTextField
                    label={t("dashboard.name")}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </AppFormField>
                <AppFormField size={{ xs: 12, sm: "auto" }}>
                  <AppActionsRow align="start">
                    <AppFileButton
                      variant="contained"
                      accept="application/pdf"
                      disabled={uploading || storageFull}
                      onFileSelected={uploadFile}
                      startIcon={<UploadOutlinedIcon />}
                      sx={formRowButtonSx}
                    >
                      {uploading ? t("common.loading") : t("guide.upload")}
                    </AppFileButton>
                    <AppButton variant="outlined" onClick={refreshDocs} sx={formRowButtonSx}>
                      {t("common.refresh")}
                    </AppButton>
                  </AppActionsRow>
                </AppFormField>
              </AppFormGrid>
            </PageSectionCard>
            </Box>
            <PageSectionCard title={t("guide.tabDocuments")}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("dashboard.name")}</TableCell>
                  <TableCell>{t("guide.fileName")}</TableCell>
                  <TableCell>{t("guide.status")}</TableCell>
                  <TableCell>{t("guide.chunks")}</TableCell>
                  <TableCell>{t("guide.error")}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>{d.title}</TableCell>
                    <TableCell>{d.fileName}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(d.status)} label={d.status || t("guide.statusUploaded")} />
                    </TableCell>
                    <TableCell>{d.chunkCount ?? "—"}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="error">
                        {d.errorMessage || ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("guide.viewData")}>
                        <AppIconButton size="small" onClick={() => void viewGuideRecord(d)}>
                          <Icon fontSize="small">visibility</Icon>
                        </AppIconButton>
                      </Tooltip>
                      <Tooltip title={t("guide.reingest")}>
                        <AppIconButton
                          size="small"
                          onClick={async () => {
                            if (!d.id) return;
                            try {
                              await guideService.reingest(d.id);
                              setDocs((prev) =>
                                prev.map((row) =>
                                  row.id === d.id ? { ...row, status: "INGESTING", errorMessage: undefined } : row
                                )
                              );
                              refreshDocs().catch(console.error);
                            } catch (e) {
                              console.error(e);
                              setError(t("guide.reingestFailed"));
                            }
                          }}
                        >
                          <Icon fontSize="small">sync</Icon>
                        </AppIconButton>
                      </Tooltip>
                      <AppIconButton
                        size="small"
                        color="error"
                        onClick={() => deleteDoc(d)}
                      >
                        <Icon fontSize="small">delete</Icon>
                      </AppIconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {docs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t("guide.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </PageSectionCard>
          </>
        )}

        {tab === 1 && (
          <>
            <Box sx={{ mb: 2 }}>
            <PageSectionCard title={t("guide.discoverIndex")}>
              <AppFormGrid alignItems="flex-end">
                <AppFormField size={{ xs: 12, md: "grow" }}>
                  <OpenSearchIndexPicker
                    label={t("guide.indexName")}
                    placeholder={t("guide.indexPlaceholder")}
                    value={indexName ? [indexName] : []}
                    onChange={(indices) => setIndexName(indices[0] ?? "")}
                    fullWidth
                  />
                </AppFormField>
                <AppFormField size={{ xs: 12, md: "auto" }}>
                  <AppActionsRow align="start">
                    <AppButton
                      variant="contained"
                      disabled={discovering || !indexName.trim() || storageFull}
                      onClick={discoverMapping}
                      sx={formRowButtonSx}
                    >
                      {discovering ? t("common.loading") : t("guide.discoverIndex")}
                    </AppButton>
                    <AppButton variant="outlined" onClick={refreshDrafts} sx={formRowButtonSx}>
                      {t("common.refresh")}
                    </AppButton>
                    <AppButton variant="outlined" color="warning" onClick={checkMappingChanges} sx={formRowButtonSx}>
                      {t("guide.checkMappingChanges")}
                    </AppButton>
                  </AppActionsRow>
                </AppFormField>
              </AppFormGrid>
              {mappingChanges.length > 0 && (
                <Alert severity="warning" sx={custom.common.marginTopTwo}>
                  {t("guide.mappingChangesFound", { count: mappingChanges.length })}
                </Alert>
              )}
            </PageSectionCard>
            </Box>
            <PageSectionCard title={t("guide.tabMapping")}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("guide.indexName")}</TableCell>
                  <TableCell>{t("dashboard.name")}</TableCell>
                  <TableCell>{t("guide.fieldCount")}</TableCell>
                  <TableCell>{t("guide.status")}</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {drafts.map((d) => {
                  const inFlight = isRagTransferInFlight(d, pendingTransferIds, transferringId);
                  const displayStatus = inFlight
                    ? "INGESTING"
                    : d.status || t("guide.statusDraft");
                  return (
                  <TableRow key={d.id}>
                    <TableCell>{d.indexName}</TableCell>
                    <TableCell>{d.draftTitle}</TableCell>
                    <TableCell>{d.fieldCount ?? "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" color={statusColor(displayStatus)} label={displayStatus} />
                    </TableCell>
                    <TableCell align="right">
                      {inFlight ? (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          <CircularProgress size={18} />
                          <Typography variant="caption" color="text.secondary">
                            {t("guide.adaptorTransferIngesting")}
                          </Typography>
                          <AppButton size="small" variant="outlined" onClick={() => openViewAnalysis(d)}>
                            {t("guide.viewAnalysis")}
                          </AppButton>
                        </Stack>
                      ) : null}
                      {!inFlight && d.status === "DRAFT" && d.id && (
                          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                            <AppButton
                              size="small"
                              variant="contained"
                              disabled={!d.analysisApprovedAt || storageFull}
                              onClick={() => approveDraft(d.id!)}
                            >
                              {t("guide.transferToRag")}
                            </AppButton>
                            <AppIconButton
                              size="small"
                              title={t("common.actions")}
                              onClick={(e) => {
                                setMenuAnchor(e.currentTarget);
                                setMenuDraft(d);
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </AppIconButton>
                          </Stack>
                        )}
                      {!inFlight && d.status === "INGESTED" && d.id && (
                          <Box sx={custom.components.guide.draftActionsRow}>
                            <AppButton size="small" variant="outlined" onClick={() => openViewAnalysis(d)}>
                              {t("guide.viewAnalysis")}
                            </AppButton>
                            <AppButton size="small" color="error" variant="outlined" onClick={() => deleteIngestedDraft(d)}>
                              {t("guide.deleteIngested")}
                            </AppButton>
                          </Box>
                        )}
                      {!inFlight && d.status === "REJECTED" && d.id && (
                          <AppButton
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => void deleteRejectedDraft(d)}
                          >
                            {t("guide.deleteRejected")}
                          </AppButton>
                        )}
                    </TableCell>
                  </TableRow>
                  );
                })}
                {drafts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        {t("guide.draftEmpty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </PageSectionCard>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor && menuDraft)}
              onClose={() => {
                setMenuAnchor(null);
                setMenuDraft(null);
              }}
            >
              <MenuItem
                onClick={() => {
                  if (!menuDraft) return;
                  const draft = menuDraft;
                  setMenuAnchor(null);
                  setMenuDraft(null);
                  openAnalysisPanel(draft);
                }}
              >
                <ListItemIcon>
                  <RateReviewOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {menuDraft?.analysisApprovedAt
                    ? t("guide.reviewAnalysis")
                    : t("guide.generateAnalysis")}
                </ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (!menuDraft?.id) return;
                  const id = menuDraft.id;
                  setMenuAnchor(null);
                  setMenuDraft(null);
                  void rejectDraft(id);
                }}
              >
                <ListItemIcon>
                  <DeleteOutlineIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>{t("guide.rejectDraft")}</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}

        {tab === 2 && <AdaptorDiscoveryTab />}

        <GuideAnalysisPanel
          open={analysisPanelOpen}
          indexName={activeDraft?.indexName}
          analysisText={analysisText}
          analysisApproved={Boolean(activeDraft?.analysisApprovedAt)}
          generating={generatingAnalysis}
          saving={savingAnalysis}
          onChange={setAnalysisText}
          onClose={closeAnalysisPanel}
          onGenerate={generateAnalysis}
          onApprove={approveAnalysis}
        />
        <GuideAnalysisViewDialog
          open={viewAnalysisOpen}
          indexName={viewAnalysisMeta.indexName}
          draftTitle={viewAnalysisMeta.draftTitle}
          analysisText={viewAnalysisText}
          loading={viewAnalysisLoading}
          onClose={() => setViewAnalysisOpen(false)}
        />
        <GuideAnalysisViewDialog
          open={viewDocOpen}
          title={t("guide.viewData")}
          draftTitle={viewDocTitle}
          analysisText={viewDocText}
          loading={viewDocLoading}
          onClose={() => setViewDocOpen(false)}
        />
    </PageListContainer>
  );
}
